import React, { useState, useEffect, useRef, useCallback } from 'react';
import { subscriptionsAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';

const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [serviceCount, setServiceCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('mtn_momo');
  const [phone, setPhone] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      subscriptionsAPI.getPlans(),
      subscriptionsAPI.getMy(),
    ]).then(([plansRes, myRes]) => {
      setPlans(plansRes.data.plans || []);
      setCurrent(myRes.data.subscription || null);
      setServiceCount(myRes.data.serviceCount || 0);
      setProductCount(myRes.data.productCount || 0);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [user, navigate]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const refreshSubscription = useCallback(async () => {
    try {
      const myRes = await subscriptionsAPI.getMy();
      setCurrent(myRes.data.subscription || null);
      setServiceCount(myRes.data.serviceCount || 0);
      setProductCount(myRes.data.productCount || 0);
    } catch (e) {}
  }, []);

  const handleSubscribe = async (planKey) => {
    if (!user) return navigate('/login');
    const plan = plans.find(p => p.key === planKey);
    if (!plan) return;

    const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;

    if (price === 0) {
      setSubscribing(true);
      try {
        await subscriptionsAPI.subscribe({ plan: planKey, billing_cycle: billingCycle });
        await refreshSubscription();
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to subscribe');
      } finally {
        setSubscribing(false);
      }
      return;
    }

    // For paid plans, show payment modal
    setSelectedPlan(plan);
    setPaymentMethod('mtn_momo');
    setPhone(user.phone || '');
    setPaymentStatus(null);
    setPaymentMessage('');
    setShowPaymentModal(true);
  };

  const startPayment = async () => {
    if (!selectedPlan) return;

    if (paymentMethod !== 'card' && !phone.trim()) {
      setPaymentMessage('Please enter your phone number');
      return;
    }

    setPaymentStatus('processing');
    setPaymentMessage('Initiating payment...');

    try {
      const res = await subscriptionsAPI.initiatePayment({
        plan: selectedPlan.key,
        billing_cycle: billingCycle,
        payment_method: paymentMethod,
        phone: paymentMethod !== 'card' ? phone.trim() : undefined,
      });

      const data = res.data;

      if (data.payment_link) {
        setPaymentMessage('Redirecting to payment page...');
        window.location.href = data.payment_link;
        return;
      }

      // MoMo payment - start polling
      setPaymentStatus('pending');
      setPaymentMessage('Please approve the payment on your phone. A push notification has been sent to your number.');

      const txId = data.transaction_id;
      let pollCount = 0;
      const maxPolls = 60;

      pollRef.current = setInterval(async () => {
        pollCount++;
        if (pollCount > maxPolls) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPaymentStatus('failed');
          setPaymentMessage('Payment timed out. Please try again.');
          return;
        }

        try {
          const verifyRes = await subscriptionsAPI.verifyPayment(txId);
          const status = verifyRes.data.status;

          if (status === 'successful') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPaymentStatus('successful');
            setPaymentMessage('Payment confirmed! Your subscription is now active.');
            await refreshSubscription();
          } else if (status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPaymentStatus('failed');
            setPaymentMessage(verifyRes.data.message || 'Payment failed. Please try again.');
          }
        } catch (e) {
          // Continue polling on network errors
        }
      }, 5000);
    } catch (err) {
      setPaymentStatus('failed');
      setPaymentMessage(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
    }
  };

  const closePaymentModal = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setShowPaymentModal(false);
    setSelectedPlan(null);
    setPaymentStatus(null);
    setPaymentMessage('');
    setSubscribing(false);
  };

  if (loading) return <LoadingSpinner />;

  const planMeta = {
    free:    { icon: '🆓', color: 'gray',    border: 'border-gray-200', btn: 'bg-gray-600 hover:bg-gray-700', ring: '' },
    test:    { icon: '🧪', color: 'orange',  border: 'border-orange-300', btn: 'bg-orange-500 hover:bg-orange-600', ring: 'ring-2 ring-orange-100' },
    basic:   { icon: '⭐', color: 'primary', border: 'border-primary-400', btn: 'bg-primary-600 hover:bg-primary-700', ring: 'ring-2 ring-primary-100' },
    premium: { icon: '💎', color: 'purple',  border: 'border-purple-400', btn: 'bg-purple-600 hover:bg-purple-700', ring: 'ring-2 ring-purple-100', popular: true },
  };

  const features = (plan) => {
    const f = [];
    f.push({ text: `${plan.max_services >= 999 ? 'Unlimited' : plan.max_services} services`, ok: true });
    f.push({ text: `${plan.max_products >= 999 ? 'Unlimited' : plan.max_products} products`, ok: true });
    f.push({ text: `${plan.max_images} images per listing`, ok: true });
    f.push({ text: `${plan.featured_listings} featured listings`, ok: plan.featured_listings > 0 });
    if (plan.key === 'free') {
      f.push({ text: 'Chat with buyers', ok: false });
    } else {
      f.push({ text: 'Chat with buyers', ok: true });
    }
    if (plan.key !== 'free') {
      f.push({ text: 'Priority support', ok: true });
      f.push({ text: 'Priority in search results', ok: true });
    }
    if (plan.key === 'premium') {
      f.push({ text: 'Verified seller badge', ok: true });
      f.push({ text: 'Analytics dashboard', ok: true });
    }
    return f;
  };

  const UsageBar = ({ label, used, max, color }) => {
    const pct = max >= 999 ? 5 : Math.min((used / max) * 100, 100);
    const isUnlimited = max >= 999;
    return (
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{label}</span>
          <span className="font-medium">{used} / {isUnlimited ? '∞' : max}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div className={`h-2 rounded-full transition-all ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : `bg-${color}-500`}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const price = selectedPlan ? (billingCycle === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly) : 0;

  return (
    <>
      <SEO title="Subscription Plans - TopInfo Rwanda" description="Choose the right plan for your business on TopInfo Rwanda" />
      <div className="bg-gray-50/50 min-h-screen">
        {/* Hero */}
        <div className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
          <div className="max-w-5xl mx-auto px-4 py-10 text-center">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Choose Your Plan</h1>
            <p className="text-primary-100 mb-6">Grow your business on TopInfo Rwanda with the right plan</p>

            <div className="inline-flex items-center bg-white/10 backdrop-blur rounded-xl p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition ${billingCycle === 'monthly' ? 'bg-white text-primary-700 shadow-sm' : 'text-white/80 hover:text-white'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition ${billingCycle === 'yearly' ? 'bg-white text-primary-700 shadow-sm' : 'text-white/80 hover:text-white'}`}
              >
                Yearly
                <span className="ml-1.5 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">SAVE 17%</span>
              </button>
            </div>
          </div>
        </div>

        {/* Current Plan Info */}
        {current && (
          <div className="max-w-5xl mx-auto px-4 -mt-4 mb-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Current Plan</p>
                  <p className="font-bold text-lg capitalize">{planMeta[current.plan]?.icon} {current.plan}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Billing</p>
                  <p className="font-bold text-lg capitalize">{current.billing_cycle || 'monthly'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Expires</p>
                  <p className="font-bold text-lg">{current.expires_at ? new Date(current.expires_at).toLocaleDateString() : 'Never'}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${current.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {current.status}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <UsageBar label="Services" used={serviceCount} max={current.max_services || 3} color="primary" />
                <UsageBar label="Products" used={productCount} max={current.max_products || 5} color="green" />
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map(plan => {
              const meta = planMeta[plan.key] || planMeta.free;
              const planPrice = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
              const isCurrentPlan = current?.plan === plan.key && current?.status === 'active';
              const ft = features(plan);

              return (
                <div key={plan.key} className={`relative bg-white rounded-2xl border-2 ${meta.border} ${meta.ring} p-6 flex flex-col transition hover:shadow-lg`}>
                  {meta.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow">
                      MOST POPULAR
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <span className="text-4xl">{meta.icon}</span>
                    <h3 className="text-xl font-bold mt-2 capitalize">{plan.label || plan.key}</h3>
                    <p className="text-gray-500 text-sm mt-1">{plan.description}</p>
                    <div className="mt-4">
                      <p className="text-3xl font-bold">
                        {planPrice === 0 ? 'Free' : `${Number(planPrice).toLocaleString()} RWF`}
                      </p>
                      {planPrice > 0 && (
                        <p className="text-sm text-gray-400">
                          /{billingCycle === 'yearly' ? 'year' : 'month'}
                          {billingCycle === 'yearly' && (
                            <span className="ml-1 text-green-500 font-medium">
                              ({Math.round(planPrice / 12).toLocaleString()}/mo)
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-2.5 flex-1 mb-6">
                    {ft.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${f.ok ? 'text-green-500' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={f.ok ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
                        </svg>
                        <span className={f.ok ? '' : 'text-gray-400'}>{f.text}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(plan.key)}
                    disabled={subscribing || isCurrentPlan}
                    className={`w-full py-3 rounded-xl text-white font-semibold transition ${meta.btn} disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isCurrentPlan ? '✓ Current Plan' : planPrice === 0 ? 'Get Started Free' : 'Subscribe Now'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto px-4 py-10">
          <h2 className="text-xl font-bold text-center mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Can I upgrade or downgrade anytime?', a: 'Yes! You can change your plan at any time. When you upgrade, your new plan starts immediately. When you downgrade, your current plan stays active until it expires.' },
              { q: 'What happens when my subscription expires?', a: 'Your listings stay visible but you won\'t be able to create new ones beyond the free plan limits until you renew.' },
              { q: 'What counts as a listing?', a: 'Services and products are counted separately. For example, on the Free plan you can have up to 3 services AND 5 products.' },
              { q: 'How do featured listings work?', a: 'Featured listings appear at the top of search results and category pages, giving your services and products more visibility.' },
              { q: 'What payment methods do you accept?', a: 'We accept MTN Mobile Money, Airtel Money, and Visa/Mastercard payments through our secure payment partner ITEC Pay.' },
            ].map(({ q, a }, i) => (
              <details key={i} className="bg-white rounded-xl border border-gray-100 group">
                <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-gray-800 list-none">
                  {q}
                  <svg className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="px-4 pb-4 text-sm text-gray-500">{a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-5xl mx-auto px-4 pb-12">
          <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-8 text-center text-white">
            <h2 className="text-xl font-bold mb-2">Need help choosing?</h2>
            <p className="text-primary-100 text-sm mb-4">Contact our team for personalized recommendations</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-primary-700 px-6 py-2.5 rounded-xl font-semibold text-sm hover:bg-gray-100 transition shadow-md">
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && !paymentStatus && closePaymentModal()}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">Complete Payment</h3>
                  <p className="text-primary-100 text-sm">
                    {selectedPlan.label} Plan — {Number(price).toLocaleString()} RWF/{billingCycle === 'yearly' ? 'year' : 'month'}
                  </p>
                </div>
                {!paymentStatus && (
                  <button onClick={closePaymentModal} className="text-white/70 hover:text-white text-xl font-bold">✕</button>
                )}
              </div>
            </div>

            <div className="p-5">
              {paymentStatus === 'successful' ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg text-green-700 mb-2">Payment Successful!</h4>
                  <p className="text-gray-500 text-sm mb-4">{paymentMessage}</p>
                  <button onClick={closePaymentModal} className="bg-primary-600 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-700 transition">
                    Done
                  </button>
                </div>
              ) : paymentStatus === 'failed' ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg text-red-700 mb-2">Payment Failed</h4>
                  <p className="text-gray-500 text-sm mb-4">{paymentMessage}</p>
                  <div className="flex gap-3 justify-center">
                    <button onClick={() => { setPaymentStatus(null); setPaymentMessage(''); }} className="bg-primary-600 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-700 transition">
                      Try Again
                    </button>
                    <button onClick={closePaymentModal} className="bg-gray-100 text-gray-700 px-6 py-2.5 rounded-xl font-medium hover:bg-gray-200 transition">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : paymentStatus === 'pending' ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg text-yellow-700 mb-2">Waiting for Payment</h4>
                  <p className="text-gray-500 text-sm mb-2">{paymentMessage}</p>
                  <div className="flex items-center justify-center gap-1 mt-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                  <button onClick={closePaymentModal} className="mt-4 text-sm text-gray-400 hover:text-gray-600 underline">
                    Cancel
                  </button>
                </div>
              ) : paymentStatus === 'processing' ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg mb-2">{paymentMessage}</h4>
                </div>
              ) : (
                <>
                  {/* Payment Method Selection */}
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Select Payment Method</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: 'mtn_momo', label: 'MTN MoMo', icon: '📱', color: 'yellow' },
                        { key: 'airtel_money', label: 'Airtel Money', icon: '📲', color: 'red' },
                        { key: 'card', label: 'Card', icon: '💳', color: 'blue' },
                      ].map(m => (
                        <button
                          key={m.key}
                          onClick={() => setPaymentMethod(m.key)}
                          className={`p-3 rounded-xl border-2 text-center transition ${
                            paymentMethod === m.key
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-2xl block mb-1">{m.icon}</span>
                          <span className="text-xs font-medium">{m.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Phone Number (for MoMo/Airtel) */}
                  {paymentMethod !== 'card' && (
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={paymentMethod === 'mtn_momo' ? '078XXXXXXX or 079XXXXXXX' : '072XXXXXXX or 073XXXXXXX'}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
                        maxLength={13}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {paymentMethod === 'mtn_momo' ? 'Enter your MTN Mobile Money number' : 'Enter your Airtel Money number'}
                      </p>
                    </div>
                  )}

                  {paymentMethod === 'card' && (
                    <div className="mb-5 bg-blue-50 p-4 rounded-xl">
                      <p className="text-sm text-blue-700">
                        You will be redirected to a secure payment page to complete your card payment.
                      </p>
                    </div>
                  )}

                  {paymentMessage && !paymentStatus && (
                    <p className="text-red-500 text-sm mb-3">{paymentMessage}</p>
                  )}

                  {/* Summary */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-5">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Plan</span>
                      <span className="font-medium">{selectedPlan.label} ({billingCycle})</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Amount</span>
                      <span className="font-bold text-lg">{Number(price).toLocaleString()} RWF</span>
                    </div>
                  </div>

                  <button
                    onClick={startPayment}
                    className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition"
                  >
                    {paymentMethod === 'card' ? 'Pay with Card' : 'Pay Now'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionPage;
